import { Inject, Injectable } from '@angular/core';
import {
  TOKENS as AUTH_TOKENS,
  IdentityService,
} from '@trustedshops/tswp-core-authorization';
import {
  TOKENS as COMMON_TOKENS,
  ExportToken,
  HttpClientService,
  LogService,
} from '@trustedshops/tswp-core-common';
import { toPromise } from '@trustedshops/tswp-core-common-eventing-rxjs';
import {
  OnboardingService,
  TOKENS,
  UserflowOnboardingIds,
} from '@trustedshops/tswp-core-masterdata';

@Injectable()
@ExportToken(TOKENS.OnboardingService)
export class OnboardingServiceImpl implements OnboardingService {
  private _onboardingIDs: UserflowOnboardingIds;
  private baseURL: string;

  public get onboardingIDs(): UserflowOnboardingIds {
    return this._onboardingIDs;
  }

  public constructor(
    @Inject(AUTH_TOKENS.IdentityService)
    private readonly _identityService: IdentityService,
    @Inject(COMMON_TOKENS.HttpClientService)
    private readonly _httpClient: HttpClientService,
    @Inject(COMMON_TOKENS.LogService)
    private readonly _logService: LogService
  ) {}

  initialize = (ids: UserflowOnboardingIds, url: string) => {
    this._onboardingIDs = ids;
    this.baseURL = url;
  };

  /**
   * Retrieves the onboarding progress for a specific user and content ID from userflow.
   * @param contentId - The identifier for the userflow onboarding content
   * @returns completion percentage between 0 to 100
   */
  getOnboardingProgress = (contentId: string): Promise<number> => {
    const { id } = this._identityService.identity.value;
    return toPromise(
      this._httpClient.get(
        `${this.baseURL}/progress?content_id=${contentId}&user_id=${id}`,
        {}
      )
    )
      .then((result: any) => {
        return Math.round(result?.progress * 100);
      })
      .catch((error) => {
        this._logService.error(
          'OnboardingServiceImpl',
          new Error(
            `Error fetching progress for content ${contentId} and user ${id}: ${JSON.stringify(
              error
            )}`
          )
        );
        return 0; // Default progress in case of error
      });
  };

  getAdvancedOnboardingProgress = () => {
    return this.getOnboardingProgress(
      this._onboardingIDs.advancedPaidAccountChecklistId
    );
  };

  getFreeOnboardingProgress = () => {
    return this.getOnboardingProgress(
      this._onboardingIDs.freeAccountChecklistId
    );
  };

  getPaidOnboardingProgress = () => {
    return this.getOnboardingProgress(
      this._onboardingIDs.paidAccountChecklistId
    );
  };
}
